"use client";

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  KeyboardEventHandler,
} from "react";
import { createPortal } from "react-dom";
import TextareaAutosize from "react-textarea-autosize";
import styled from "styled-components";
import Flex from "../../Flex";
import Select from "../../Select";
import type { SelectOption } from "../../Select";
import Text from "../../Text";
import { useEditingContext, useCellPosition } from "./EditableTableContext";
import { parseCurrencyInput, filterCurrencyChars } from "./coerceValueForPaste";
import Checkbox from "../../Checkbox";
import { Badge } from "../../Badge";
import PopupMenu from "../../PopupMenu";
import { getColor } from "../../utils";

export type EditableCellType =
  | "text"
  | "longtext"
  | "number"
  | "percent"
  | "currency"
  | "boolean"
  | "select"
  | "multiselect"
  | "date"
  | "datetime-local";

type Props = {
  type: EditableCellType;
  value: string | number | boolean;
  /** @deprecated Provided automatically via CellPositionContext from EditableTable. */
  rowId?: string;
  /** @deprecated Provided automatically via CellPositionContext from EditableTable. */
  colIndex?: number;
  onCommit: (value: string | number | boolean) => void;
  options?: SelectOption<string>[];
  placeholder?: string;
  /** When true, renders the display value in gray to indicate it is inherited from a parent row. */
  isInherited?: boolean;
  /** Symbol to display for currency cells (e.g. "$", "€", "£"). Defaults to "$". */
  currencySymbol?: string;
  /** If provided, shown in display mode instead of the computed value string.
   *  Useful for showing a formatted date while keeping the raw value for the edit input. */
  displayValue?: string;
};

const CellInput = styled.input<{ maxWidth?: number }>`
  width: 100%;
  max-width: ${p => (p.maxWidth ? `${p.maxWidth}px` : "none")};
  padding: 4px 0;
  font-size: inherit;
  line-height: 1.4;
  outline: none;
  background: transparent;
  font-family: inherit;
`;

const FloatingTextarea = styled(TextareaAutosize)`
  background: white;
  border-radius: 4px;
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.08),
    0 8px 24px rgba(0, 0, 0, 0.12);
  padding: 12px 16px;
  font-size: 13px;
  line-height: 1.6;
  outline: none;
  resize: none;
  z-index: 9999;
  font-family: inherit;
  color: #111;
  min-width: 300px;
`;

const DisplayText = styled.div<{ clickable?: boolean; $inherited?: boolean }>`
  width: 100%;
  line-height: 1.4;
  cursor: ${p => (p.clickable ? "text" : "default")};
  user-select: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${p => (p.$inherited ? getColor("grayDark") : "inherit")};
`;

const Placeholder = styled.span`
  color: #aaa;
  font-style: italic;
`;

const SelectWrapper = styled.div<{ $inherited?: boolean }>`
  width: 100%;
  button p {
    color: ${p => (p.$inherited ? getColor("grayDark") : "inherit")} !important;
  }
`;

const MultiSelectCellTrigger = styled.button<{ $inherited?: boolean }>`
  display: flex;
  align-items: center;
  gap: 3px;
  width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  overflow: hidden;
  opacity: ${p => (p.$inherited ? 0.5 : 1)};
  text-align: left;
  font-family: inherit;
  font-size: inherit;
`;

const MultiSelectCheckboxItem = styled(Flex).attrs({
  alignItems: "center",
  px: "s",
  height: "32px",
})`
  cursor: pointer;
  &:hover {
    background: ${getColor("grayLighter")};
  }
`;

const UnitLabel = styled(Text)`
  font-size: inherit;
  color: ${getColor("grayDarker")};
  flex-shrink: 0;
  pointer-events: none;
  user-select: none;
`;

const EditableTableCell: React.FC<Props> = ({
  type,
  value,
  rowId: rowIdProp,
  colIndex: colIndexProp,
  onCommit,
  options = [],
  placeholder = "Double-click to edit",
  isInherited,
  currencySymbol = "$",
  displayValue,
}) => {
  const pos = useCellPosition();
  const rowId = rowIdProp ?? pos.rowId;
  const colIndex = colIndexProp ?? (pos.colIndex >= 0 ? pos.colIndex : 0);

  const {
    editingCell,
    editingInitialChar,
    startEdit,
    cancelEdit,
    advanceCell,
    tabCell,
    isCellSelected,
    selectCell,
    registerCell,
    unregisterCell,
  } = useEditingContext();

  const isActive = editingCell?.rowId === rowId && editingCell?.colIndex === colIndex;
  const isSelected = isCellSelected(rowId, colIndex);
  const [multiselectOpen, setMultiselectOpen] = useState(false);
  // Tracks when Escape is pressed so the onBlur handler doesn't commit a discarded edit.
  const isDiscardingRef = useRef(false);
  // Register this cell's metadata with the context for copy/paste/keyboard support
  useEffect(() => {
    registerCell(rowId, colIndex, {
      value,
      type,
      onCommit,
      options,
      currencySymbol,
    });
    return () => unregisterCell(rowId, colIndex);
  }, [
    rowId,
    colIndex,
    value,
    type,
    onCommit,
    options,
    currencySymbol,
    registerCell,
    unregisterCell,
  ]);

  // For longtext: measure the cell position so the portal textarea can be fixed-positioned
  const longtextWrapperRef = useRef<HTMLDivElement>(null);
  const [longtextRect, setLongtextRect] = useState<DOMRect | null>(null);
  useLayoutEffect(() => {
    if (isActive && type === "longtext" && longtextWrapperRef.current) {
      setLongtextRect(longtextWrapperRef.current.getBoundingClientRect());
    } else {
      setLongtextRect(null);
    }
  }, [isActive, type]);

  const commit = useCallback(
    (raw: string | boolean) => {
      if (type === "number" || type === "percent" || type === "currency") {
        const n = type === "currency" ? parseCurrencyInput(String(raw)) : parseFloat(String(raw));
        onCommit(isNaN(n) ? String(raw) : n);
      } else {
        onCommit(raw);
      }
      cancelEdit();
    },
    [type, onCommit, cancelEdit]
  );

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement> = useCallback(
    e => {
      if (e.key === "Escape") {
        isDiscardingRef.current = true;
        cancelEdit();
        return;
      }
      if (e.key === "Tab") {
        e.preventDefault();
        commit((e.target as HTMLInputElement | HTMLTextAreaElement).value);
        tabCell(rowId, colIndex, e.shiftKey);
        return;
      }
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && type === "longtext") {
        commit((e.target as HTMLTextAreaElement).value);
        advanceCell(rowId, colIndex, e.shiftKey);
        return;
      }
      if (e.key === "Enter" && type !== "longtext") {
        commit((e.target as HTMLInputElement).value);
        advanceCell(rowId, colIndex, e.shiftKey);
        return;
      }
    },
    [commit, type, cancelEdit, advanceCell, tabCell, rowId, colIndex]
  );

  const handleCurrencyChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.currentTarget.value = filterCurrencyChars(e.currentTarget.value);
  }, []);

  // --- Boolean: first click selects, second click toggles ---
  if (type === "boolean") {
    const boolValue = value === true || value === "true";
    return (
      <div
        style={{ opacity: isInherited ? 0.5 : 1 }}
        onClickCapture={e => {
          if (!isSelected) {
            // Intercept: select the cell but don't toggle the checkbox
            e.stopPropagation();
            e.preventDefault();
            selectCell(rowId, colIndex, e.metaKey || e.ctrlKey);
          }
          // When already selected, let the click through to toggle
        }}>
        <Checkbox
          isSelected={boolValue}
          onClick={() => {
            onCommit(!boolValue);
            cancelEdit(); // refocus the table so Enter key works after toggling
          }}
        />
      </div>
    );
  }

  // --- Multiselect: always interactive, popup with checkboxes ---
  if (type === "multiselect") {
    const values = value ? String(value).split(",").filter(Boolean) : [],
      menuSource = `ms-cell-${rowId}-${colIndex}`;

    const handleToggle = (val: string) => {
      const next = values.includes(val) ? values.filter(v => v !== val) : [...values, val];
      onCommit(next.join(","));
    };

    return (
      <>
        <MultiSelectCellTrigger
          type="button"
          $inherited={isInherited}
          data-popup-source={menuSource}
          onClick={() => setMultiselectOpen(o => !o)}>
          {values.length > 0 ? (
            values.map(v => {
              const opt = options.find(o => o.value === v);
              return (
                <Badge
                  key={v}
                  text={opt?.label ?? v}
                  background="grayLight"
                  textColor="black"
                  font="ui.small"
                />
              );
            })
          ) : (
            <Placeholder>{placeholder}</Placeholder>
          )}
        </MultiSelectCellTrigger>
        {multiselectOpen && (
          <PopupMenu source={menuSource} close={() => setMultiselectOpen(false)} stayOpenOnClick>
            {options.map(opt => (
              <MultiSelectCheckboxItem key={opt.value}>
                <Checkbox
                  isSelected={values.includes(opt.value)}
                  label={opt.label}
                  onClick={() => handleToggle(opt.value)}
                />
              </MultiSelectCheckboxItem>
            ))}
          </PopupMenu>
        )}
      </>
    );
  }

  // --- Select: always interactive ---
  if (type === "select") {
    return (
      <SelectWrapper $inherited={isInherited}>
        <Select
          value={String(value)}
          size="small"
          width="100%"
          onChange={v => {
            onCommit(v);
            cancelEdit();
          }}
          options={options}
        />
      </SelectWrapper>
    );
  }

  if (isActive) {
    if (type === "longtext") {
      return (
        <div ref={longtextWrapperRef} style={{ width: "100%" }}>
          {longtextRect &&
            createPortal(
              <FloatingTextarea
                autoFocus
                style={{
                  position: "fixed",
                  top: longtextRect.top - 16,
                  left: longtextRect.left - 16,
                  width: longtextRect.width + 116,
                }}
                defaultValue={editingInitialChar ?? String(value)}
                onFocus={e => {
                  const len = e.currentTarget.value.length;
                  e.currentTarget.setSelectionRange(len, len);
                }}
                onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) => {
                  if (isDiscardingRef.current) {
                    isDiscardingRef.current = false;
                    return;
                  }
                  commit(e.currentTarget.value);
                }}
                onKeyDown={handleKeyDown as KeyboardEventHandler<HTMLTextAreaElement>}
                minRows={4}
              />,
              document.body
            )}
        </div>
      );
    }

    if (type === "date" || type === "datetime-local") {
      return (
        <CellInput
          autoFocus
          type={type}
          defaultValue={String(value)}
          onBlur={e => commit(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
          style={{ flex: 1 }}
        />
      );
    }

    const isNumeric = type === "number" || type === "percent" || type === "currency";
    const rawValue =
      type === "percent"
        ? String(value).replace(/%$/, "")
        : type === "currency"
          ? value !== ""
            ? Number(value).toFixed(2)
            : ""
          : String(value);

    return (
      <Flex alignItems="center" width="100%">
        {type === "currency" && <UnitLabel aria-hidden="true">{currencySymbol}</UnitLabel>}
        <CellInput
          autoFocus
          maxWidth={type === "currency" ? 100 : undefined}
          type={type === "currency" ? "text" : isNumeric ? "number" : "text"}
          inputMode={type === "currency" ? "decimal" : undefined}
          defaultValue={editingInitialChar ?? rawValue}
          onChange={type === "currency" ? handleCurrencyChange : undefined}
          onBlur={e => commit(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
          style={{ flex: 1 }}
        />
        {type === "percent" && <UnitLabel aria-hidden="true">%</UnitLabel>}
      </Flex>
    );
  }

  const displayStr =
    displayValue !== undefined
      ? displayValue
      : type === "currency" && value !== ""
        ? `${currencySymbol}${Number(value).toFixed(2)}`
        : type === "percent" && value !== ""
          ? `${value}%`
          : String(value);

  return (
    <DisplayText
      clickable
      $inherited={isInherited}
      onDoubleClick={() => startEdit(rowId, colIndex)}>
      {displayStr || <Placeholder>{placeholder}</Placeholder>}
    </DisplayText>
  );
};

export default EditableTableCell;
